import { cartApi } from "@api";
import {
    BOOKING_DATE_FORMAT,
    BOOKING_SYSTEM_DEFAULT_BOOKING_TIME_ID,
    BOOKING_SYSTEM_IBIS,
    BOOKING_SYSTEM_RESPAX_V2,
    BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS,
} from "@constants";
import {
    BookingData,
    CheckoutType,
    ICheckoutContext,
    IAvailabilityByProductAndRange,
    IAvailabilityByRange,
    IAvailField,
    IBookingAvailability,
    IBookingCheckInOptions,
    IBookingDropOffLocation,
    IBookingPickUpLocation,
    IBookingPickUpTime,
    ICartItemData,
    ICartItemFareData,
    ICartItemsByGroup,
    ICartItemsByProduct,
    IPriceInfo,
    ITotalNetRateInfo,
    ITotalRRPInfo,
    ITourFareBookingTime,
    IUpdateCartItemData,
    IAddItemBase,
} from "@interfaces";
import { useWindowDimensions } from "@utilities";
import _ from "lodash";
import moment from "moment";
import { quoteApi } from "src/Api/quoteApi";

export const buildCartCheckoutContext = (): ICheckoutContext => {
    return {
        checkoutType: CheckoutType.CART,
    };
};

export const buildQuoteCheckoutContext = (quoteId: string): ICheckoutContext => {
    return {
        checkoutType: CheckoutType.QUOTE,
        quoteId,
    };
};

export const buildBookNowCheckoutContext = (): ICheckoutContext => {
    return {
        checkoutType: CheckoutType.BOOK_NOW,
    };
};

export const isMobileViewHandler = () => {
    const { width } = useWindowDimensions();
    return width < 768;
};

export const CheckoutCartStepLabels: string[] = ["Cart", "Checkout"];
export const QuoteStepLabels: string[] = ["Items", "Checkout"];
export const BookNowStepLabels: string[] = ["Item", "Checkout"];

export const isAvailabiltyByRange = (availability: IBookingAvailability): boolean => {
    return "productPricingData" in availability;
};

export const getCartItemFareData = (cartItem: ICartItemData): ICartItemFareData => {
    const farePrice = isAvailabiltyByRange(cartItem.availability)
        ? cartItem.product.json.faresprices.find(
              (fare) =>
                  fare.productPricesDetailsId.toString() ===
                  cartItem.product_price_details_id.toString(),
          )
        : cartItem.product.json.faresprices.find(
              (fare) =>
                  fare.fareTypeId.toString() ===
                  (
                      cartItem.availability as IAvailabilityByProductAndRange
                  ).FarePrice!.fareTypeId.toString(),
          );
    return {
        id: farePrice!.fareTypeId.toString(),
        name: farePrice!.fareName,
        bookingSystem: farePrice!.bookingSystem,
    };
};

export const getCartItemUnitRRP = (cartItem: ICartItemData): number | null => {
    return isAvailabiltyByRange(cartItem.availability)
        ? (cartItem.availability as IAvailabilityByRange).productPricingData.RRP
        : ((cartItem.availability as IAvailabilityByProductAndRange).FarePrice?.RRP ?? null);
};

export const getCartItemRRP = (cartItem: ICartItemData): number => {
    const unitRRP = getCartItemUnitRRP(cartItem);
    return (unitRRP ?? 0) * cartItem.booking_quantity;
};

export const getCartItemUnitNetRate = (cartItem: ICartItemData): number | null => {
    return isAvailabiltyByRange(cartItem.availability)
        ? (cartItem.availability as IAvailabilityByRange).productPricingData.NetRate
        : ((cartItem.availability as IAvailabilityByProductAndRange).FarePrice?.NetRate ?? null);
};

export const getCartItemNetRate = (cartItem: ICartItemData): number => {
    const unitNetRate = getCartItemUnitNetRate(cartItem);
    return (unitNetRate ?? 0) * cartItem.booking_quantity;
};

export const getTotalRRP = (cartItems: ICartItemData[]): ITotalRRPInfo => {
    return {
        totalRRP: cartItems.reduce((total, cartItem) => {
            return total + getCartItemRRP(cartItem);
        }, 0),
        hasMissingPrices: cartItems.some((cartItem) => {
            return getCartItemUnitRRP(cartItem) === null;
        }),
    };
};

export const getTotalNetRate = (cartItems: ICartItemData[]): ITotalNetRateInfo => {
    return {
        totalNetRate: cartItems.reduce((total, cartItem) => {
            return total + getCartItemNetRate(cartItem);
        }, 0),
        hasMissingPrices: cartItems.some((cartItem) => {
            return getCartItemUnitNetRate(cartItem) === null;
        }),
    };
};

export const getItemsPriceInfo = (cartItems: ICartItemData[]): IPriceInfo => {
    const rrpInfo = getTotalRRP(cartItems);
    const netRateInfo = getTotalNetRate(cartItems);

    const commission = rrpInfo.totalRRP - netRateInfo.totalNetRate;
    const commissionPercent = rrpInfo.totalRRP ? (commission / rrpInfo.totalRRP) * 100 : 0;

    return {
        totalRRP: rrpInfo.totalRRP,
        totalNetRate: netRateInfo.totalNetRate,
        commission,
        commissionPercent,
        totalPrice: rrpInfo.totalRRP,
        hasMissingPrices: rrpInfo.hasMissingPrices || netRateInfo.hasMissingPrices,
    };
};

export const groupCartItemsByGroup = (cartItems: ICartItemData[]): ICartItemsByGroup[] => {
    const ret = cartItems.reduce((acc: ICartItemsByGroup[], cartItem) => {
        const group = acc.find((group) => group.group_id === cartItem.group_id);
        if (group) {
            group.items.push(cartItem);
        } else {
            acc.push({
                group_id: cartItem.group_id ?? null,
                items: [cartItem],
            });
        }

        return acc;
    }, []);
    return ret;
};

export const groupCartItemsByProduct = (cartItems: ICartItemData[]): ICartItemsByProduct[] => {
    return cartItems.reduce((acc: ICartItemsByProduct[], cartItem) => {
        const product = acc.find(
            (product) => product.productId.toString() === cartItem.tdms_product_id.toString(),
        );
        const fareData = getCartItemFareData(cartItem);
        if (product) {
            const existingItemsWithThisFare = product.itemsByFare.find(
                (fare) => fare.id === fareData.id,
            );
            if (existingItemsWithThisFare) {
                // For Accommodation (class 'A'), we need to group items by group_id
                // For other product classes, always create a new group
                if (product.productClass === "A") {
                    const group = existingItemsWithThisFare.itemsByGroup.find(
                        (group) => group.group_id === cartItem.group_id,
                    );
                    if (group) {
                        group.items.push(cartItem);
                    } else {
                        existingItemsWithThisFare.itemsByGroup.push({
                            group_id: cartItem.group_id ?? null,
                            items: [cartItem],
                        });
                    }
                } else {
                    // For non-Accommodation products, always create a new group
                    existingItemsWithThisFare.itemsByGroup.push({
                        group_id: cartItem.group_id ?? null,
                        items: [cartItem],
                    });
                }
            } else {
                product.itemsByFare.push({
                    ...fareData,
                    itemsByGroup: [
                        {
                            group_id: cartItem.group_id ?? null,
                            items: [cartItem],
                        },
                    ],
                });
            }
        } else {
            const fareData = getCartItemFareData(cartItem);
            acc.push({
                productId: cartItem.tdms_product_id.toString(),
                productClass: cartItem.product.json.productClass,
                name: cartItem.product.json.name,
                productImagePath: cartItem.product.json.productImagePath,
                supplierName: cartItem.product.json.supplierName,
                startLocationName: cartItem.product.json.categories.startLocationName,
                itemsByFare: [
                    {
                        ...fareData,
                        itemsByGroup: [
                            {
                                group_id: cartItem.group_id ?? null,
                                items: [cartItem],
                            },
                        ],
                    },
                ],
            });
        }
        return acc;
    }, []);
};

export const formatBookingDatesByProductClass = (
    bookingDates: string[],
    productClass: string,
): string => {
    if (!bookingDates?.length) return "";

    const sortedDates = bookingDates
        .map((date) => moment(date, ["YYYY-MM-DD", "DD-MMM-YYYY"]))
        .sort((a, b) => a.valueOf() - b.valueOf());

    // For Tours (Y)
    if (productClass === "Y") {
        return `Starts on ${sortedDates.map((date) => date.format("MMM D")).join(", ")}`;
    }

    // For Activities (V)
    if (productClass === "V") {
        return sortedDates.map((date) => date.format("MMM D")).join(", ");
    }

    // For Accommodation (A)
    if (productClass === "A") {
        const ranges: string[] = [];
        let rangeStart = sortedDates[0];
        let prevDate = sortedDates[0];

        for (let i = 1; i <= sortedDates.length; i++) {
            const currentDate = sortedDates[i];

            // Check if dates are not consecutive or if we've reached the end
            if (!currentDate || currentDate.diff(prevDate, "days") > 1) {
                // Add one day to the last date for checkout
                const rangeEnd = prevDate.clone().add(1, "day");

                // Format the range
                ranges.push(
                    rangeStart.isSame(prevDate)
                        ? `${rangeStart.format("MMM D")} - ${rangeEnd.format("MMM D")}`
                        : `${rangeStart.format("MMM D")} - ${rangeEnd.format("MMM D")}`,
                );

                // Start new range if there are more dates
                if (currentDate) {
                    rangeStart = currentDate;
                }
            }

            if (currentDate) {
                prevDate = currentDate;
            }
        }

        return ranges.join(", ");
    }

    return "";
};

export const getCheckInDateItem = (items: ICartItemData[]) =>
    items
        .sort(
            (a, b) =>
                moment(a.booking_date, BOOKING_DATE_FORMAT).valueOf() -
                moment(b.booking_date, BOOKING_DATE_FORMAT).valueOf(),
        )
        .at(0);

export const getCheckInAvail = (avails: IBookingAvailability[]) =>
    avails
        .sort(
            (a, b) =>
                moment(a.BookingDate, BOOKING_DATE_FORMAT).valueOf() -
                moment(b.BookingDate, BOOKING_DATE_FORMAT).valueOf(),
        )
        .at(0);

export const checkBookingNeedsPickUpTimeID = (bookingSystem: string) =>
    BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS.includes(bookingSystem.toLowerCase());

export const getNumPax = (item: ICartItemData): number => {
    if (isAvailabiltyByRange(item.availability)) {
        return Number(
            item.product.json.faresprices.find(
                (fp) => fp.productPricesDetailsId === item.product_price_details_id,
            )?.numPax ?? "1",
        );
    } else {
        return Number(
            item.product.json.faresprices.find(
                (fp) =>
                    fp.productPricesDetailsId ===
                    (item.availability as IAvailabilityByProductAndRange).FarePrice?.fareTypeId,
            )?.numPax ?? "1",
        );
    }
};

export const getAvailsCheckInOptions = async (
    bookingSystem: string,
    checkInAvail: IBookingAvailability,
    checkInBookingDetails: ITourFareBookingTime,
) => {
    if (bookingSystem.toLowerCase() === BOOKING_SYSTEM_RESPAX_V2) {
        const availabilityByRange = checkInAvail as IAvailabilityByRange;
        const respaxPickUpTimeInfos: IBookingPickUpTime[] = availabilityByRange.fields
            .filter((bt) => bt.name.trim().toLowerCase() === "timeid")
            .map((bt) => ({
                displayName: bt.displayName,
                options: bt.options.map((o) => ({
                    id: o.externalId.toString(),
                    displayName: o.displayName,
                    isDefault: false, // TODO: calculate later
                    downstreamTimeRef: o.externalId.toString(), // as used to get details to confirm availability
                })),
            }));
        const respaxDetailsToConfirmReqs = _.uniqBy(
            respaxPickUpTimeInfos.flatMap((i) => i.options),
            (opt) => opt.id,
        ).map((bookingTime) =>
            cartApi
                .confirmAvailability(
                    availabilityByRange.productPricesDetailsId.toString(),
                    checkInAvail.BookingDate,
                    bookingTime.id,
                )
                .then((resp) => ({
                    timeId: bookingTime.id,
                    fields: resp.data.fields,
                })),
        );
        const respaxDetailsToConfirmResps = await Promise.allSettled(respaxDetailsToConfirmReqs);
        respaxDetailsToConfirmResps.filter((p) => p.status === "rejected").forEach(console.error);
        const fieldsWithTimeID = respaxDetailsToConfirmResps
            .filter(
                (p): p is PromiseFulfilledResult<{ timeId: string; fields: IAvailField[] }> =>
                    p.status === "fulfilled",
            )
            .map((p) => p.value)
            .flat();
        const respaxPickupLocationInfos: IBookingPickUpLocation[] = fieldsWithTimeID
            .map((f) => ({
                ...f,
                fields: f.fields.filter((f: any) => f.name.toLowerCase() === "pickupid"),
            }))
            .flatMap((f) =>
                f.fields.map((ff: IAvailField) => ({
                    displayName: ff.displayName,
                    upstreamTimeRef: f.timeId,
                    options: ff.options.map((o) => ({
                        id: o.externalId.toString(),
                        displayName: o.displayName,
                    })),
                })),
            );

        const respaxDropOffLocationInfos: IBookingDropOffLocation[] = fieldsWithTimeID
            .map((f) => ({
                ...f,
                fields: f.fields.filter((f: IAvailField) => f.name.toLowerCase() === "dropoffs"),
            }))
            .flatMap((f) =>
                f.fields.map((ff: IAvailField) => ({
                    displayName: ff.displayName,
                    upstreamTimeRef: f.timeId,
                    options: ff.options.map((o) => ({
                        id: o.externalId.toString(),
                        displayName: o.displayName,
                    })),
                })),
            );
        return {
            pickupTimes: respaxPickUpTimeInfos,
            pickupLocations: respaxPickupLocationInfos,
            dropoffLocations: respaxDropOffLocationInfos,
        };
    } else if (bookingSystem.toLowerCase() === BOOKING_SYSTEM_IBIS) {
        const bookingTimeRef = BOOKING_SYSTEM_DEFAULT_BOOKING_TIME_ID;
        const ppdid = isAvailabiltyByRange(checkInAvail)
            ? (checkInAvail as IAvailabilityByRange).productPricesDetailsId.toString()
            : (
                  checkInAvail as IAvailabilityByProductAndRange
              ).FarePrice?.productPricesDetailsId.toString();

        if (_.isNil(ppdid)) {
            return null;
        }

        const ibisDetailsToConfirmReqs = await cartApi.confirmAvailability(
            ppdid,
            checkInAvail.BookingDate,
            bookingTimeRef,
        );
        const ibisPickUpTimeInfos: IBookingPickUpTime[] = ibisDetailsToConfirmReqs.data.fields
            .filter((f) => f.name.toLowerCase() === "timeid")
            .map((f) => ({
                displayName: f.displayName,
                options: f.options.map((o) => ({
                    id: o.externalId.toString(),
                    displayName: o.displayName,
                    isDefault: false,
                    downstreamTimeRef: bookingTimeRef,
                })),
            }));
        const ibisPickUpLocationInfos: IBookingPickUpLocation[] =
            ibisDetailsToConfirmReqs.data.fields
                .filter((f) => f.name.toLowerCase() === "pickupid")
                .map((f) => ({
                    displayName: f.displayName,
                    upstreamTimeRef: bookingTimeRef,
                    options: f.options.map((o) => ({
                        id: o.externalId.toString(),
                        displayName: o.displayName,
                    })),
                }));
        return {
            pickupTimes: ibisPickUpTimeInfos,
            pickupLocations: ibisPickUpLocationInfos,
            dropoffLocations: [],
        };
    } else {
        const genericPickUpTimInfo: IBookingPickUpTime = {
            displayName: "Booking Time",
            options: checkInBookingDetails.bookingTimes.map((bt) => ({
                id: bt.BookingTimeID.toString(),
                displayName: bt.BookingTime,
                isDefault: bt.Default,
                downstreamTimeRef: null,
            })),
        };

        const genericPickUpLocationInfo: IBookingPickUpLocation = {
            displayName: "Pickup",
            upstreamTimeRef: null,
            options: checkInBookingDetails.pickupLocations.map((pl) => ({
                id: pl.PickupID!.toString(),
                displayName: `${pl.PickupTime} ${pl.PickupLocation}`,
            })),
        };

        return {
            pickupTimes: [genericPickUpTimInfo],
            pickupLocations: [genericPickUpLocationInfo],
            dropoffLocations: [],
        };
    }
};

export const getBookingCheckInOptions = async (
    checkInDateItem: ICartItemData,
): Promise<IBookingCheckInOptions | null> => {
    const farePrice = getCartItemFareData(checkInDateItem);
    const checkInOptions = await getAvailsCheckInOptions(
        farePrice.bookingSystem,
        checkInDateItem.availability,
        checkInDateItem.booking_details,
    );
    if (_.isNil(checkInOptions)) return null;
    return {
        fareTypeId: farePrice.id,
        productPricesDetailsId: checkInDateItem.product_price_details_id.toString(),
        ...checkInOptions,
    };
};

// validate that all items have same number of booking_data and booking_quantity
// valdate that all booking_data have at least one redeemers
// validate that all booking_data have redeemers less than numPax of availability of that item
// for each item find the check in option and validate that booking_data has timeId, picupId and dropOffLocations if any
interface IValidation {
    isValid: boolean;
    message?: string;
}

export const validateCheckoutStep = (
    itemsByProducts: ICartItemsByProduct[],
    checkInOptions: IBookingCheckInOptions[],
): IValidation => {
    // Validate booking_data length matches booking_quantity for each item
    for (const product of itemsByProducts) {
        for (const fare of product.itemsByFare) {
            for (const group of fare.itemsByGroup) {
                for (const item of group.items) {
                    if (item.booking_data.length !== item.booking_quantity) {
                        return {
                            isValid: false,
                            message: `${product.name}: Number of booking details (${item.booking_data.length}) does not match booking quantity (${item.booking_quantity})`,
                        };
                    }

                    // Find matching check-in option for this item's fare
                    const checkInOption = checkInOptions.find(
                        (opt) =>
                            opt.fareTypeId === fare.id &&
                            opt.productPricesDetailsId === item.product_price_details_id.toString(),
                    );

                    // Validate redeemers exist and are within limits
                    for (const bookingData of item.booking_data) {
                        if (!bookingData.redeemers || bookingData.redeemers.length === 0) {
                            return {
                                isValid: false,
                                message: `${product.name}: Missing traveler information for one or more bookings`,
                            };
                        }

                        if (bookingData.redeemers.length > getNumPax(item)) {
                            return {
                                isValid: false,
                                message: `${product.name}: Number of travelers (${bookingData.redeemers.length}) exceeds available capacity (${item.availability.NumAvailable})`,
                            };
                        }

                        if (checkInOption) {
                            if (
                                checkInOption.pickupTimes.flatMap((pt) => pt.options).length > 0 &&
                                !bookingData.timeId
                            ) {
                                return {
                                    isValid: false,
                                    message: `${product.name}: Missing booking time selection`,
                                };
                            }

                            if (
                                checkInOption.pickupLocations.flatMap((pl) => pl.options).length >
                                    0 &&
                                !bookingData.pickupId
                            ) {
                                return {
                                    isValid: false,
                                    message: `${product.name}: Missing pickup location selection`,
                                };
                            }

                            if (
                                checkInOption.dropoffLocations.flatMap((d) => d.options).length >
                                    0 &&
                                !bookingData.dropoffId
                            ) {
                                return {
                                    isValid: false,
                                    message: `${product.name}: Missing drop-off location selection`,
                                };
                            }
                        }

                        for (const optionalField of item.booking_details.optionalFields) {
                            if (optionalField.mandatory) {
                                const value =
                                    bookingData.optionalData?.[optionalField.optionalFieldId];
                                if (
                                    _.isEmpty(value) ||
                                    (typeof value === "string" && value.trim() === "")
                                ) {
                                    return {
                                        isValid: false,
                                        message: `${product.name}: Missing optional data for “${optionalField.title}”`,
                                    };
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // If all validations pass
    return {
        isValid: true,
    };
};

export const transformCartItemsToUpdateCartItemsData = (
    items: ICartItemData[],
): IUpdateCartItemData[] => {
    return items.map((item) => ({
        cartItemId: item.id,
        quantity: item.booking_quantity,
        bookingData: item.booking_data.map((bd) => ({
            ...bd,
            pickupId: bd.pickupId && bd.pickupId !== "undefined" ? String(bd.pickupId) : null,
        })),
    }));
};

export const transformCartItemToAddItemBase = (items: ICartItemData[]): IAddItemBase[] => {
    return items.map((item) => ({
        tdmsProductId: item.tdms_product_id.toString(),
        productPricesDetails: [
            {
                productPricesDetailsId: item.product_price_details_id.toString(),
                quantityDetails: [
                    {
                        quantity: item.booking_quantity,
                        bookingDate: moment(item.booking_date).format("DD-MMM-YYYY"),
                        bookingData: item.booking_data,
                        timeId: item.time_id?.toString(),
                    } as any,
                ],
            },
        ],
        startDate: moment(item.start_date).format("DD-MMM-YYYY"),
        days: item.days,
        selectedAvailableIndices: [item.selected_index],
        timeId: item.time_id ?? null,
    }));
};

export const updateItems = async (
    items: IUpdateCartItemData[],
    mode: "quote" | "cart",
    quoteId?: string,
) => {
    switch (mode) {
        case "cart": {
            const saveCartItemsResponse = await cartApi.updateCartItems(items); // send the whole array
            return saveCartItemsResponse.data; // assuming .data is the array of responses
        }
        case "quote": {
            const response = await quoteApi.updateQuoteItems(items, Number(quoteId));
            return response.data;
        }
    }
};

export const buildBookingDataForNewQuantity = (
    quantityIndex: number,
    timeId: string,
): BookingData => ({
    timeId,
    quantityIndex,
    commences: null,
    pickupId: null,
    optionalData: [],
});
export const buildItemsForNewQuantity = (
    items: ICartItemData[],
    newQuantity: number,
    originalItems: ICartItemData[],
): ICartItemData[] => {
    if (newQuantity < 1) {
        throw new Error("New quantity must be greater than 0");
    }

    return items.map((item) => ({
        ...item,
        booking_quantity: newQuantity,
        booking_data:
            newQuantity < item.booking_quantity
                ? item.booking_data.slice(0, newQuantity) // remove excess booking data
                : [
                      ...item.booking_data,
                      ..._.range(item.booking_quantity, newQuantity).map((newQuantityIndex) => {
                          const quantityIndex = newQuantityIndex + 1;
                          const originalItem = originalItems.find((oi) => oi.id === item.id);
                          const existingBookingData = originalItem?.booking_data?.[quantityIndex]; // populate booking data from original item
                          return (
                              existingBookingData ||
                              buildBookingDataForNewQuantity(
                                  quantityIndex,
                                  item.booking_data[0].timeId,
                              )
                          );
                      }),
                  ],
    }));
};

/**
 * Parse date time in the time zone but returns date and time in local time zone
 * This is useful for date time comparison in the local time zone
 * Example: 2025-10-01T00:00:00+10:00 is Oct 1 in AEST but Sep 30 of NPT so returned date is Oct 1 in NPT
 * @param date - date in format "2025-06-30T10:00:00Z"
 * @returns date in format "2025-06-30T10:00:00"
 */
export const stripTimeZone = (date: string) => {
    const originalTravelDate = moment.parseZone(date);
    return moment({
        year: originalTravelDate.year(),
        month: originalTravelDate.month(), // zero-indexed in Moment.js
        day: originalTravelDate.date(),
        hour: originalTravelDate.hour(),
        minute: originalTravelDate.minute(),
        second: originalTravelDate.second(),
    });
};
